/**
 * `mountViewport` — the 3D half of the editor.
 *
 * This module owns the frame: it drives the clock, decides what each layer should show, and
 * arbitrates the pointer between the four things that want it (gizmo, IK handles, bone picking,
 * region reassignment). The layers themselves know nothing about each other.
 *
 * Render policy: redraw only when something changed. A `dirty` flag is raised by the store, by
 * editor events, by orbit/gizmo interaction, by resizes, and unconditionally while playing. The
 * *update* half of the loop still runs every frame — it is cheap, and skipping it is how a
 * viewport ends up one frame behind its own gizmo.
 */

import { Box3, Group, Vector2 } from 'three';

import './viewport.css';
import { CANONICAL_BONE_IDS } from '../../shared';
import { icon, type IconName } from '../panels/dom';
import type { CameraPreset, EditorContext, ViewportHandle } from '../context';
import type { EditorMode, SkinTool } from '../types';
import { createGizmoService, createBoneGizmo } from './gizmos';
import { createHipsHandleLayer, type ViewportHipsProbe } from './hipsHandle';
import { createIkHandleLayer } from './ikHandles';
import { createCompanionLayer, createModelLayer } from './modelLayer';
import { createOnionLayer } from './onionLayer';
import { createPoseDriver } from './poseDriver';
import { createViewportScene } from './scene';
import { createSkeletonLayer } from './skeletonLayer';
import { createSkinHeatLayer } from './skinHeatLayer';

/** Pointer travel (CSS px) above which a press counts as a drag, not a click. */
const CLICK_SLOP = 4;

interface Hint {
  key: string;
  what: string;
}

/** Shared by every mode: framing and the camera presets are always live. */
const HINT_COMMON: ReadonlyArray<Hint> = [
  { key: 'F', what: 'frame' },
  { key: '1/3/7', what: 'views' },
];

/**
 * The shortcut HUD, bottom left — what works HERE, not every key the app has.
 *
 * `G` translates joints in RIG only and `R`/`K`/`Space` mean nothing outside ANIMATE, so listing
 * all five everywhere advertised four dead keys in four of the six modes (audit A5 note).
 */
const HINT_BY_MODE: Partial<Record<EditorMode, ReadonlyArray<Hint>>> = {
  rig: [{ key: 'G', what: 'move joint' }],
  animate: [
    { key: 'R', what: 'rotate' },
    { key: 'K', what: 'key' },
    { key: 'Space', what: 'play' },
  ],
};

/** Fallback for the modes with no viewport editing of their own. */
const HINT_VIEW_ONLY: ReadonlyArray<Hint> = [{ key: '5', what: 'persp' }];

/**
 * Container width (CSS px) below which the camera-preset buttons drop their words. Measured: at
 * 640px the full labels plus the info chip overflow the bar, and the chip is what a user reads.
 */
const NARROW_BAR_WIDTH = 760;

export function mountViewport(
  container: HTMLElement,
  ctx: EditorContext,
): ViewportHandle & ViewportHipsProbe {
  container.classList.add('asx-viewport');

  const view = createViewportScene(container);
  const canvas = view.renderer.domElement;

  let dirty = true;
  const requestRender = (): void => {
    dirty = true;
  };

  // --- layers -------------------------------------------------------------

  const modelLayer = createModelLayer(view.modelGroup, ctx);

  /*
   * Companions get their own top-level group, attached here rather than in `scene.ts`.
   * `modelGroup` is what the GLB exporter walks, and `overlayGroup` is editor-only chrome — a
   * read-only preview character is neither, so it needs a third home that nothing else inspects.
   */
  const companionGroup = new Group();
  companionGroup.name = 'asx-companions';
  view.scene.add(companionGroup);
  const companionLayer = createCompanionLayer(companionGroup, ctx);

  const poseDriver = createPoseDriver(ctx);
  const onionLayer = createOnionLayer(view.overlayGroup, ctx);
  const skeletonLayer = createSkeletonLayer(view.overlayGroup, ctx);
  const skinHeat = createSkinHeatLayer(view.overlayGroup, ctx);
  const gizmoService = createGizmoService({
    camera: view.camera,
    domElement: canvas,
    scene: view.scene,
    orbit: view.controls,
    requestRender,
  });
  const boneGizmo = createBoneGizmo(view.overlayGroup, ctx);
  const ikHandles = createIkHandleLayer(view.overlayGroup, ctx);
  const hipsHandle = createHipsHandleLayer(view.overlayGroup, ctx);

  modelLayer.sync();
  companionLayer.sync(modelLayer.bounds());

  /** The open model plus every companion — what `F` has to fit once previews are in the scene. */
  function framedBounds(): Box3 | null {
    const model = modelLayer.bounds();
    const companions = companionLayer.bounds();
    if (!model) return companions;
    if (!companions) return model;
    return model.clone().union(companions);
  }

  function frameModel(): void {
    view.frameBounds(framedBounds());
    requestRender();
  }

  function setCameraPreset(preset: CameraPreset): void {
    view.setCameraPreset(preset);
    requestRender();
  }

  // --- overlay chrome -----------------------------------------------------
  //
  // Display switches, camera presets, the asset readout and the shortcut HUD
  // (`design/ui-design.md` §4, "Viewport"). The three switches are *viewport-local* — they hide
  // layers, they do not write editor state — so nothing outside this file has to know about them.

  const display = { skeleton: true, weights: true, mesh: true, onion: false };

  function applyDisplay(): void {
    skeletonLayer.setVisible(display.skeleton);
    skinHeat.setVisible(display.weights);
    view.modelGroup.visible = display.mesh;
    // Companions are meshes too: one switch hides every body in the scene, not just the open one.
    companionLayer.setVisible(display.mesh);
    onionLayer.setVisible(display.onion);
    requestRender();
  }

  /**
   * `short` is the label the button falls back to in a narrow viewport — the word lives in a
   * span so CSS can swap the two without this file knowing the container's width.
   */
  function viewportButton(
    label: string,
    title: string,
    glyph: IconName | null,
    onClick: () => void,
    short?: string,
  ): HTMLButtonElement {
    const node = document.createElement('button');
    node.type = 'button';
    node.className = 'asx-vbtn';
    node.title = title;
    if (glyph) node.appendChild(icon(glyph));
    if (label) {
      const text = document.createElement('span');
      text.className = 'asx-vlabel';
      text.textContent = label;
      node.appendChild(text);
    }
    if (short) {
      const abbr = document.createElement('span');
      abbr.className = 'asx-vshort';
      abbr.textContent = short;
      node.appendChild(abbr);
    }
    node.addEventListener('click', onClick);
    return node;
  }

  function divider(): HTMLElement {
    const node = document.createElement('span');
    node.className = 'asx-vdiv';
    return node;
  }

  const bar = document.createElement('div');
  bar.className = 'asx-viewport-bar';

  /**
   * Which modes a switch means anything in. A button the current mode cannot act on is hidden —
   * `display` is untouched, so the layer keeps whatever state the user left it in and coming
   * back to the mode shows the switch exactly as they set it.
   *
   * `weights` is RIG-only because `skinHeatLayer` is: its `active()` gate reads
   * `mode === 'rig' && rig.skinGenerated`, so the switch draws nothing anywhere else.
   */
  const TOGGLE_MODES: Partial<Record<keyof typeof display, ReadonlyArray<EditorMode>>> = {
    weights: ['rig'],
    onion: ['animate'],
  };

  const toggles: Array<{ node: HTMLButtonElement; key: keyof typeof display }> = (
    [
      { key: 'skeleton', label: 'Skeleton', glyph: 'bone' },
      { key: 'mesh', label: 'Mesh', glyph: 'box' },
      { key: 'weights', label: 'Weights', glyph: 'sliders' },
      // Onion skinning is the one switch that starts OFF: it only means anything in ANIMATE with
      // a keyed clip open, and it costs a rig re-pose whenever the playhead crosses a key.
      {
        key: 'onion',
        label: 'Onion',
        glyph: 'layers',
        title: 'Ghost skeletons at the previous and next keyed frames (ANIMATE)',
      },
    ] as const
  ).map((entry) => {
    const { key, label, glyph } = entry;
    const title = 'title' in entry ? entry.title : `Show ${label.toLowerCase()}`;
    const node = viewportButton(label, title, glyph, () => {
      display[key] = !display[key];
      applyDisplay();
      syncChrome();
    });
    node.dataset.display = key;
    bar.appendChild(node);
    return { node, key };
  });

  bar.appendChild(divider());
  // Perspective is icon-only: it is the default view, and the word is the one the row can spare.
  bar.appendChild(
    viewportButton('', 'Perspective view', 'camera', () => setCameraPreset('persp')),
  );
  for (const preset of ['front', 'side', 'top'] as const) {
    const label = preset[0].toUpperCase() + preset.slice(1);
    bar.appendChild(
      viewportButton(label, `${label} view`, null, () => setCameraPreset(preset), label[0]),
    );
  }
  bar.appendChild(viewportButton('', 'Frame the model (F)', 'frameAll', () => frameModel()));

  const info = document.createElement('span');
  info.className = 'asx-vinfo';
  bar.appendChild(info);
  container.appendChild(bar);

  const hint = document.createElement('div');
  hint.className = 'asx-viewport-hint';
  container.appendChild(hint);

  /** Triangles across every mesh of the imported model — the readout's middle figure. */
  function triangleCount(): number {
    const model = ctx.session.model;
    if (!model) return 0;
    let total = 0;
    for (const mesh of [...model.meshes, ...model.skinnedMeshes]) {
      const geometry = mesh.geometry;
      const count = geometry.index?.count ?? geometry.getAttribute('position')?.count ?? 0;
      total += Math.floor(count / 3);
    }
    return total;
  }

  /**
   * The third figure of the readout: how many joints the thing on screen actually has.
   *
   * `sourceBoneNames` is the SOURCE file's bone list, which is empty for a model this tool rigged
   * itself — so an auto-rigged character used to advertise "0 joints" (audit §A). The bound rig is
   * the honest answer whenever there is one; the source count is only meaningful before binding.
   */
  function jointsLabel(model: NonNullable<typeof ctx.session.model>): string {
    const rig = ctx.session.rig;
    if (rig) {
      const bound = rig.bones.size;
      return `${bound > 0 ? bound : CANONICAL_BONE_IDS.length} joints`;
    }
    if (model.sourceBoneNames.length > 0) return `${model.sourceBoneNames.length} source bones`;
    return 'unrigged';
  }

  /** Repaint the chrome from state. DOM only — safe to call from a store listener. */
  function syncChrome(): void {
    const state = ctx.store.getState();

    for (const { node, key } of toggles) {
      node.classList.toggle('on', display[key]);
      const modes = TOGGLE_MODES[key];
      // Hiding the BUTTON only. `display[key]` is deliberately left alone: flipping the layer
      // here would silently undo the user's choice every time they changed mode.
      node.hidden = !!modes && !modes.includes(state.mode);
    }

    const model = ctx.session.model;
    info.textContent = model
      ? `${state.project?.name ?? 'model'} · ${triangleCount().toLocaleString('en-US')} tris · ${jointsLabel(model)}`
      : 'no model loaded';
    info.title = info.textContent;

    const selected = state.mode === 'rig' ? state.rig.selectedJoint : state.selection.bone;
    hint.replaceChildren();
    for (const entry of [...HINT_COMMON, ...(HINT_BY_MODE[state.mode] ?? HINT_VIEW_ONLY)]) {
      const key = document.createElement('kbd');
      key.textContent = entry.key;
      hint.append(key, ` ${entry.what}`, separator());
    }
    if (selected) {
      const name = document.createElement('b');
      name.textContent = selected;
      hint.append(name, ' selected');
    } else {
      hint.append('nothing selected');
    }
  }

  /**
   * Below `NARROW_BAR_WIDTH` the camera presets show their initial instead of their word, so the
   * bar stays one line and the info chip keeps its text. Driven by the scene's existing
   * ResizeObserver rather than a second one; a zero-width container (never laid out, jsdom) is
   * not "narrow", it is unmeasured, so the default layout stands.
   */
  function syncBarWidth(): void {
    const width = container.clientWidth;
    if (width > 0) container.classList.toggle('narrow', width < NARROW_BAR_WIDTH);
  }

  function separator(): HTMLElement {
    const node = document.createElement('span');
    node.className = 'sep';
    node.textContent = '·';
    return node;
  }

  applyDisplay();
  syncChrome();
  syncBarWidth();

  // --- subscriptions ------------------------------------------------------

  const unsubscribers: Array<() => void> = [];

  // Store listeners fire synchronously inside actions: only ever raise a flag here. Anything
  // that needs to dispatch an action is deferred to the RAF loop below.
  unsubscribers.push(ctx.store.subscribe(() => requestRender()));

  // A resize observed by the scene clears the drawing buffer; without this the viewport stays
  // blank until some unrelated store change happens to raise `dirty` (audit A6).
  unsubscribers.push(view.onResize(requestRender));

  // Same observer, second listener: the bar's compact layout is a function of container width.
  unsubscribers.push(view.onResize(syncBarWidth));

  // The reassign tool is a click tool, not a drag tool: the only affordance it gets is the
  // cursor. Set once here and then only on change, so orbiting never fights a style write.
  function syncCursor(tool: SkinTool): void {
    canvas.style.cursor = tool === 'reassign' ? 'crosshair' : '';
  }
  syncCursor(ctx.store.getState().rig.skinTool);
  unsubscribers.push(ctx.store.select((s) => s.rig.skinTool, syncCursor));

  // The overlay chrome is pure DOM, so it can be repainted straight from a store listener.
  unsubscribers.push(ctx.store.select((s) => s.selection.bone, syncChrome));
  unsubscribers.push(ctx.store.select((s) => s.rig.selectedJoint, syncChrome));
  unsubscribers.push(ctx.store.select((s) => s.mode, syncChrome));
  unsubscribers.push(ctx.store.select((s) => s.project, syncChrome));

  unsubscribers.push(
    ctx.events.on('model-changed', () => {
      modelLayer.sync();
      skinHeat.invalidate();
      // The open model sets the companion spacing, so a new one re-lays out the row. By this
      // point the preview controller has already cleared companions if the project changed.
      companionLayer.sync(modelLayer.bounds());
      // `sync()` re-parents the model root, so the Mesh switch has to be re-applied over it.
      applyDisplay();
      syncChrome();
      frameModel();
    }),
  );
  unsubscribers.push(
    ctx.events.on('companions-changed', () => {
      companionLayer.sync(modelLayer.bounds());
      applyDisplay();
      requestRender();
    }),
  );
  unsubscribers.push(
    ctx.events.on('rig-changed', () => {
      modelLayer.sync();
      skinHeat.invalidate();
      // Generating skin / confirming the rig swaps the displayed mesh, which moves the open
      // model's bounds — and those set the companion spacing. Without this re-sync the preview
      // characters keep the old row spacing and end up overlapping or floating away.
      companionLayer.sync(modelLayer.bounds());
      applyDisplay();
      // The readout's joint count comes from the bound rig, and this is the event that binds it.
      syncChrome();
      requestRender();
    }),
  );
  unsubscribers.push(
    ctx.events.on('skin-changed', () => {
      skinHeat.invalidate();
      requestRender();
    }),
  );
  unsubscribers.push(
    ctx.events.on('camera-preset', (payload) => {
      if (payload === 'persp' || payload === 'front' || payload === 'side' || payload === 'top') {
        setCameraPreset(payload);
      }
    }),
  );
  unsubscribers.push(ctx.events.on('request-frame', () => requestRender()));

  const onControlsChange = (): void => requestRender();
  view.controls.addEventListener('change', onControlsChange);
  gizmoService.controls.addEventListener('change', onControlsChange);

  // --- pointer ------------------------------------------------------------

  const ndc = new Vector2();
  let pointerInside = false;
  let pointerValid = false;
  let downX = 0;
  let downY = 0;
  let downButton = -1;

  function toNdc(event: PointerEvent): void {
    const rect = canvas.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) {
      pointerValid = false;
      return;
    }
    ndc.set(
      ((event.clientX - rect.left) / rect.width) * 2 - 1,
      -((event.clientY - rect.top) / rect.height) * 2 + 1,
    );
    pointerValid = true;
  }

  function handleClick(): void {
    if (!pointerValid) return;
    const state = ctx.store.getState();

    if (ikHandles.pick(ndc, view.camera)) {
      hipsHandle.release();
      requestRender();
      return;
    }

    // Between the IK targets and the skeleton: it sits ON the Hips joint, so the joint's own pick
    // sphere would otherwise swallow every click meant for the height handle.
    if (hipsHandle.pick(ndc, view.camera)) {
      requestRender();
      return;
    }

    const bone = skeletonLayer.pick(ndc, view.camera);
    if (bone) {
      // Any bone pick — the Hips included — hands the shared gizmo back to rotation. Without this
      // one grab of the height handle would outrank the bone gizmo forever.
      hipsHandle.release();
      if (state.mode === 'rig') {
        // RIG carries two selections: the joint being placed and the canonical bone the panels
        // show. Keep them together so the hierarchy list never disagrees with the gizmo.
        ctx.store.batch(() => {
          ctx.store.actions.selectJoint(bone);
          ctx.store.actions.selectBone(bone);
        });
      } else {
        ctx.store.actions.selectBone(bone);
      }
      requestRender();
      return;
    }

    // Nothing else claimed the click: in RIG the reassign tool takes it. `skinBusy` gates the
    // call — a second re-solve queued on top of a running one is how the mesh and the weights
    // end up describing different things. `display.mesh` gates it too: three's raycaster happily
    // hits an invisible mesh, and reassigning a region the user cannot see is not an edit they
    // meant to make.
    if (
      state.mode === 'rig' &&
      state.rig.skinTool === 'reassign' &&
      state.rig.skinGenerated &&
      !state.rig.skinBusy &&
      display.mesh
    ) {
      const pick = skinHeat.pick(ndc, view.camera);
      if (!pick) return;
      const targetBone = state.rig.selectedJoint ?? state.selection.bone;
      if (!targetBone) {
        ctx.store.actions.toast('info', 'Select a joint first');
        return;
      }
      void ctx.rigTools.reassignRegionAt(0, pick.vertexIndex, targetBone);
      requestRender();
    }
  }

  const onPointerDown = (event: PointerEvent): void => {
    pointerInside = true;
    toNdc(event);
    downButton = event.button;
    downX = event.clientX;
    downY = event.clientY;
  };

  const onPointerMove = (event: PointerEvent): void => {
    pointerInside = true;
    toNdc(event);
  };

  const onPointerUp = (event: PointerEvent): void => {
    if (downButton !== 0 || event.button !== 0) return;
    downButton = -1;
    const moved = Math.hypot(event.clientX - downX, event.clientY - downY);
    if (moved > CLICK_SLOP || gizmoService.isHot()) return;
    toNdc(event);
    handleClick();
  };

  const onPointerEnter = (): void => {
    pointerInside = true;
  };

  const onPointerLeave = (): void => {
    pointerInside = false;
    pointerValid = false;
    if (ctx.store.getState().hover) ctx.store.actions.hoverBone(null);
    requestRender();
  };

  canvas.addEventListener('pointerdown', onPointerDown);
  canvas.addEventListener('pointermove', onPointerMove);
  canvas.addEventListener('pointerup', onPointerUp);
  canvas.addEventListener('pointerenter', onPointerEnter);
  canvas.addEventListener('pointerleave', onPointerLeave);

  // --- keyboard -----------------------------------------------------------

  function keyboardIsOurs(event: KeyboardEvent): boolean {
    if (event.ctrlKey || event.metaKey || event.altKey) return false;
    const target = event.target as HTMLElement | null;
    if (target) {
      const tag = target.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable) {
        return false;
      }
    }
    // Space / Delete / Ctrl-Z belong to the timeline and the shell; we only claim keys while the
    // viewport is actually the thing the user is pointing at or focused on.
    return pointerInside || document.activeElement === canvas;
  }

  const onKeyDown = (event: KeyboardEvent): void => {
    if (!keyboardIsOurs(event)) return;
    const mode = ctx.store.getState().mode;
    switch (event.key) {
      case 'f':
      case 'F':
        frameModel();
        break;
      case '1':
        setCameraPreset('front');
        break;
      case '3':
        setCameraPreset('side');
        break;
      case '7':
        setCameraPreset('top');
        break;
      case '5':
        setCameraPreset('persp');
        break;
      case 'r':
      case 'R':
        if (mode !== 'animate') return;
        boneGizmo.setAnimateMode('rotate');
        requestRender();
        break;
      case 'g':
      case 'G':
        // Translate exists in RIG only. In ANIMATE it would author data the clip cannot hold.
        if (mode !== 'rig') return;
        requestRender();
        break;
      default:
        return;
    }
    event.preventDefault();
  };

  window.addEventListener('keydown', onKeyDown);

  // --- frame loop ---------------------------------------------------------

  let raf = 0;
  let lastTime = 0;
  let disposed = false;

  function loop(now: number): void {
    if (disposed) return;
    raf = requestAnimationFrame(loop);

    const deltaSeconds = lastTime === 0 ? 0 : (now - lastTime) / 1000;
    lastTime = now;

    if (ctx.store.getState().playhead.playing) {
      poseDriver.tick(deltaSeconds);
      dirty = true;
    }

    if (modelLayer.sync()) {
      skinHeat.invalidate();
      dirty = true;
    }

    poseDriver.apply();
    // IMMEDIATELY after the pose driver and before every other reader: the onion layer poses the
    // real rig at the neighbouring keys and puts it back, so nothing may read world matrices
    // between these two calls.
    onionLayer.update();
    skeletonLayer.update();
    const draggingKey = gizmoService.isDragging() ? gizmoService.currentKey() : null;
    ikHandles.update(draggingKey);
    hipsHandle.update(draggingKey);
    skinHeat.update();

    // IK handles outrank the bone gizmo: if the user grabbed a hand target, that is what the
    // one shared TransformControls should be holding. The hips-height handle sits between them —
    // it is only ever claimed by an explicit grab, and a bone pick releases it again.
    const boneClaim = boneGizmo.claim();
    gizmoService.setClaim(ikHandles.claim() ?? hipsHandle.claim() ?? boneClaim);
    boneGizmo.sync(gizmoService.isDragging());

    updateHover();

    if (dirty) {
      view.render();
      dirty = false;
    }
  }

  function updateHover(): void {
    if (!pointerInside || !pointerValid || gizmoService.isHot()) return;
    const state = ctx.store.getState();
    const bone = skeletonLayer.pick(ndc, view.camera);
    if (bone !== state.hover) ctx.store.actions.hoverBone(bone);
  }

  raf = requestAnimationFrame(loop);

  // --- handle -------------------------------------------------------------

  return {
    renderer: view.renderer,
    /*
     * The one read-only window this handle offers onto a layer, added for the headless surface:
     * a WebGL drag cannot be automated, so "is the hips handle where I think it is, and is it the
     * thing holding the gizmo?" has to be answerable without a pointer (plan D24, the same reason
     * `testSurface.solveIk` exists).
     */
    hipsHandle: () => hipsHandle.probe(),
    resize(): void {
      view.resize();
      requestRender();
    },
    setCameraPreset,
    frameModel,
    dispose(): void {
      if (disposed) return;
      disposed = true;
      cancelAnimationFrame(raf);

      window.removeEventListener('keydown', onKeyDown);
      canvas.removeEventListener('pointerdown', onPointerDown);
      canvas.removeEventListener('pointermove', onPointerMove);
      canvas.removeEventListener('pointerup', onPointerUp);
      canvas.removeEventListener('pointerenter', onPointerEnter);
      canvas.removeEventListener('pointerleave', onPointerLeave);
      view.controls.removeEventListener('change', onControlsChange);
      gizmoService.controls.removeEventListener('change', onControlsChange);
      for (const off of unsubscribers) off();
      unsubscribers.length = 0;

      ikHandles.dispose();
      hipsHandle.dispose();
      boneGizmo.dispose();
      gizmoService.dispose();
      skinHeat.dispose();
      skeletonLayer.dispose();
      onionLayer.dispose();
      poseDriver.dispose();
      // Detach only — the companion roots belong to `previewController`, which disposes them.
      companionLayer.dispose();
      view.scene.remove(companionGroup);
      modelLayer.dispose();
      view.dispose();

      hint.remove();
      bar.remove();
      container.classList.remove('asx-viewport', 'narrow');
    },
  };
}
